import { KeyValuePair } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import {
	AppMappingStepConfigurationSaveModel,
	AiIntegrationFlowValidationResultModel,
	AiValidationInputModel,
	AppIntegrationFlowValidationResultModel,
	GlobalSearchIntegrationFlowValidationResultModel,
	GlobalSearchValidationInputModel,
	IntegrationFlowConfigurationValidateResponseModel,
	IntegrationFlowKind,
	IntegrationFlowRequestResultModel,
	MappingStepConfigurationSaveModel,
	MappingStepConfigurationWithAiSaveModel,
	MappingStepConfigurationWithAppSaveModel,
	MappingStepConfigurationWithGlobalSearchSaveModel,
	PerItemRequestStepConfigurationSaveModel,
	ResultMapperColorConditionModel,
	ResultMapperStyledFieldSaveModel,
	RequestMethod,
	RequestStepConfigurationSaveModel,
	StepKind,
	ResultMapperColor
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { TagProps } from "client/Components";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { isNil, isNotNil } from "client/Misc/utils";
import {
	IntegrationFlowAfasAuthenticationSettings,
	IntegrationFlowAuthenticationKind,
	IntegrationFlowClientCertificateAuthenticationSettings,
	IntegrationFlowOAuth2AuthenticationSettings,
	KeyedValueSaveModel
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { Base64FileUriUploadWithMetadataModel } from "client/Misc/commonTypes";

export const stepStatusToLocalizationKey: Record<StepStatus, LocalizationKey> = {
	["active"]: "_Active",
	["error"]: "_Error",
	["missing-data"]: "_MissingData"
};

export type GlobalSearchInputData = {
	kind: IntegrationFlowKind.GlobalSearch;
	searchTermInput: GlobalSearchValidationInputModel;
};

export type AiInputData = {
	kind: IntegrationFlowKind.Ai;
	searchTermInput: AiValidationInputModel;
};

export type InputData =
	| GlobalSearchInputData
	| AiInputData;

export type RequestStepConfiguration = {
	kind: StepKind.Request;
} & OverrideProperties<
	RequestStepConfigurationSaveModel,
	{
		queryParameters: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
		headers: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
	}
>;

export type PerItemRequestStepConfiguration = {
	kind: StepKind.PerItemRequest;
} & OverrideProperties<
	PerItemRequestStepConfigurationSaveModel,
	{
		maxItems: number | null;
		queryParameters: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
		headers: readonly ObjectWithClientId<KeyValuePair<string, string>>[];
	}
>;

export type MappingStepConfiguration = {
	kind: StepKind.Mapping;
	mapping: MappingStepConfigurationSaveModel | null;
};

export type ResultMapperColorConditionModelFormData = ObjectWithClientId<OverrideProperties<
	ResultMapperColorConditionModel,
	{
		color: ResultMapperColor | null;
	}
>>;

export type ResultMapperStyledFieldFormData = OverrideProperties<
	ResultMapperStyledFieldSaveModel,
	{
		colors: readonly ResultMapperColorConditionModelFormData[];
	}
>
	& {
		shouldIncludeColors: boolean;
	};

export type AppMappingStepConfigurationFormData = OverrideProperties<
	AppMappingStepConfigurationSaveModel,
	{
		title: ResultMapperStyledFieldFormData;
		date: ResultMapperStyledFieldFormData;
		subtitle1: ResultMapperStyledFieldFormData;
		subtitle2: ResultMapperStyledFieldFormData;
		subtitle3: ResultMapperStyledFieldFormData;
		description: ResultMapperStyledFieldFormData;
	}
>;

export type MappingStepConfigurationWithAppFormData = OverrideProperties<
	MappingStepConfigurationWithAppSaveModel,
	{
		app: AppMappingStepConfigurationFormData;
	}
>;

export type StepConfiguration =
	| RequestStepConfiguration
	| PerItemRequestStepConfiguration
	| MappingStepConfiguration;

export type StepStatus =
	| "active"
	| "error"
	| "missing-data";

export type HttpStatusKind =
	| "successful"
	| "error";

export function isGlobalSearchMappingStep(
	mappingStep: MappingStepConfigurationSaveModel
): mappingStep is MappingStepConfigurationWithGlobalSearchSaveModel {
	return isNotNil(
		(mappingStep as MappingStepConfigurationWithGlobalSearchSaveModel).globalSearch
	);
}

export function isAiMappingStep(
	mappingStep: MappingStepConfigurationSaveModel
): mappingStep is MappingStepConfigurationWithAiSaveModel {
	return isNotNil(
		(mappingStep as MappingStepConfigurationWithAiSaveModel).ai
	);
}

export function isAppMappingStep(
	mappingStep: MappingStepConfigurationSaveModel
): mappingStep is MappingStepConfigurationWithAppSaveModel {
	return isNotNil(
		(mappingStep as MappingStepConfigurationWithAppSaveModel).app
	);
}

export type IntegrationFlowApiKeyAuthenticationFormData = {
	headers: readonly ObjectWithClientId<KeyedValueSaveModel>[];
	queryParameters: readonly ObjectWithClientId<KeyedValueSaveModel>[];
};

export type IntegrationFlowClientCertificateAuthenticationSettingsFormData = OverrideProperties<
	IntegrationFlowClientCertificateAuthenticationSettings,
	{
		certificate: OptionalModel<Base64FileUriUploadWithMetadataModel | null>;
	}
>
	& { initialFileName: string; };

export type HostFormModel = ObjectWithClientId<{
	value: string;
}>;

export type AuthenticationFormData = {
	name: string;
	kind: IntegrationFlowAuthenticationKind;
	apiKey: IntegrationFlowApiKeyAuthenticationFormData;
	oAuth2: IntegrationFlowOAuth2AuthenticationSettings;
	clientCertificate: IntegrationFlowClientCertificateAuthenticationSettingsFormData;
	afas: IntegrationFlowAfasAuthenticationSettings;
	allowedHosts: OptionalFormDataModel<readonly HostFormModel[]>;
};

export function getStatusCodeKind(
	statusCode: number
): HttpStatusKind {
	if (statusCode >= 200 && statusCode <= 299) {
		return "successful";
	}

	if (statusCode >= 400 && statusCode <= 599) {
		return "error";
	}

	console.error(`Status code ${statusCode} is not supported`);

	return "error";
};

// Not localized, this is a technical standard
export const methodToTextMap: Record<RequestMethod, string> = {
	[RequestMethod.Get]: "GET",
	[RequestMethod.Post]: "POST"
};

export function getRequestDescription(step: RequestStepConfiguration | PerItemRequestStepConfiguration): string {
	let {
		method,
		queryParameters,
		url
	} = step;

	if (queryParameters.length > 0) {
		try {
			const builder = new URL(url);
			for (const parameter of queryParameters) {
				builder.searchParams
					.set(parameter.key, parameter.value);
			}
			// searchParams.set will encode the values, so we decode it again
			builder.search = decodeURIComponent(builder.search);
			url = builder.toString();
		} catch (e) { /* Ignore error, show the url field */}
	}

	return methodToTextMap[method] + " | " + url;
}

export type StepTagConfiguration = Pick<TagProps, "text" | "color">;

export function getRequestStepTag(
	hasError: boolean,
	clientId: Guid,
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>
): StepTagConfiguration | undefined {
	return statusToTagConfigurationMap[
		hasError
			? "error"
			: responseToStepStatus(stepResults.get(clientId)) ?? "unknown"
	];
};

export const statusToTagConfigurationMap: Record<StepStatus | "unknown", StepTagConfiguration | undefined> = {
	["unknown"]: undefined,
	["active"]: { text: localize(stepStatusToLocalizationKey.active), color: "success" },
	["error"]: { text: localize(stepStatusToLocalizationKey.error), color: "danger" },
	["missing-data"]: { text: localize(stepStatusToLocalizationKey["missing-data"]), color: "warning" }
};

function responseToStepStatus(
	response: IntegrationFlowRequestResultModel | undefined
): StepStatus | undefined {
	if (!response || !response.statusCode) {
		return;
	}

	const statusCodeKind: HttpStatusKind = getStatusCodeKind(response?.statusCode);

	switch(statusCodeKind) {
		case "error":
			return "error";
		case "successful":
			return "active";
		default:
			never(statusCodeKind);
	}
}

export function isGlobalSearchValidationResults(result: IntegrationFlowConfigurationValidateResponseModel): result is GlobalSearchIntegrationFlowValidationResultModel {
	return isNotNil(
		(result as GlobalSearchIntegrationFlowValidationResultModel).globalSearchSample
	);
};

export function isAppValidationResults(result: IntegrationFlowConfigurationValidateResponseModel): result is AppIntegrationFlowValidationResultModel {
	return isNotNil(
		(result as AppIntegrationFlowValidationResultModel).appSample
	);
};

export function isAiValidationResults(result: IntegrationFlowConfigurationValidateResponseModel): result is AiIntegrationFlowValidationResultModel {
	return isNotNil(
		(result as AiIntegrationFlowValidationResultModel).aiSample
	);
};

export function getResultMapperStepStatus(
	step: ObjectWithClientId<MappingStepConfiguration>,
	validateResponse: IntegrationFlowConfigurationValidateResponseModel | null,
	stepWithErrorIds: ReadonlySet<Guid>
): StepStatus | "unknown" {
	const clientId: Guid = getClientId(step);
	const hasError: boolean = stepWithErrorIds.has(clientId);

	if (hasError) {
		return "error";
	}

	if (isNil(validateResponse) || isNil(step.mapping)) {
		return "unknown";
	}

	if (isGlobalSearchValidationResults(validateResponse)
		&& isGlobalSearchMappingStep(step.mapping)
	) {
		if (!validateResponse.globalSearchSample
			.isSet
		) {
			return "unknown";
		}

		const {
			title,
			link,
			dateTime,
			description,
			subTitle1,
			subTitle2
		} = validateResponse
			.globalSearchSample
			.value;

		const {
			titleExpression,
			linkExpression,
			dateExpression,
			descriptionExpression,
			subtitle1Expression,
			subtitle2Expression
		} = step
			.mapping
			.globalSearch;

		const hasMissingData: boolean = [
			[titleExpression, title],
			[linkExpression, link],
			[dateExpression, dateTime],
			[descriptionExpression, description],
			[subtitle1Expression, subTitle1],
			[subtitle2Expression, subTitle2],
		].some(
			([formValue, sampleValue]) => isNotNil(formValue)
				&& Boolean(formValue)
				&& isNil(sampleValue)
		);

		return hasMissingData
			? "missing-data"
			: "active";
	}

	if (isAppValidationResults(validateResponse)
		&& isAppMappingStep(step.mapping)
	) {
		if (!validateResponse.appSample
			.isSet
		) {
			return "unknown";
		}

		const {
			title,
			link,
			dateTime,
			description,
			subTitle1,
			subTitle2,
			subTitle3
		} = validateResponse
			.appSample
			.value;

		const {
			title: titleField,
			linkExpression,
			date: dateField,
			description: descriptionField,
			subtitle1: subtitle1Field,
			subtitle2: subtitle2Field,
			subtitle3: subtitle3Field
		} = step
			.mapping
			.app;

		const hasMissingData: boolean = [
			[titleField.expression, title?.value],
			[linkExpression, link],
			[dateField?.expression, dateTime?.value],
			[descriptionField?.expression, description?.value],
			[subtitle1Field?.expression, subTitle1?.value],
			[subtitle2Field?.expression, subTitle2?.value],
			[subtitle3Field?.expression, subTitle3?.value]
		].some(
			([formValue, sampleValue]) => isNotNil(formValue)
				&& Boolean(formValue)
				&& isNil(sampleValue)
		);

		return hasMissingData
			? "missing-data"
			: "active";
	}

	if (isAiValidationResults(validateResponse)
		&& isAiMappingStep(step.mapping)
	) {
		if (!validateResponse.aiSample
			.isSet
		) {
			return "unknown";
		}

		const {
			content,
			id,
			title,
			link,
			dateTime
		} = validateResponse
			.aiSample
			.value;

		const {
			contentExpression,
			idExpression,
			titleExpression,
			linkExpression,
			dateExpression
		} = step
			.mapping
			.ai;

		const hasMissingData: boolean = [
			[contentExpression, content],
			[idExpression, id],
			[titleExpression, title],
			[linkExpression, link],
			[dateExpression, dateTime]
		].some(
			([formValue, sampleValue]) => isNotNil(formValue)
				&& Boolean(formValue)
				&& isNil(sampleValue)
		);

		return hasMissingData
			? "missing-data"
			: "active";
	}

	return "unknown";
}

export function tryParseErrorResult<TModel>(
	error: unknown
): TryResult<TModel> {
	if (!(error instanceof HttpRequestError && error.statusCode === 409)) {
		return { wasSuccessful: false };
	}

	try {
		const result: TModel = error.readJson<TModel>();

		return {
			wasSuccessful: true,
			result
		};
	} catch (e) {
		console.error(e);
		return { wasSuccessful: false };
	}
}

export const integrationFlowKindToLocalizationKey: {[key in IntegrationFlowKind]: LocalizationKey} = {
	[IntegrationFlowKind.GlobalSearch]: "_Search",
	[IntegrationFlowKind.App]: "_App",
	[IntegrationFlowKind.Ai]: "_AiAssistant"
};

export type RequestStepKind = Extract<StepKind,
	| StepKind.Request
	| StepKind.PerItemRequest
>;
